import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch {
        // Silently fall back to defaultEvents on error
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const defaultEvents = [
    {
      id: '1',
      title: 'Sunday School',
      date: 'Every Sunday',
      time: '10:00 AM CST',
      location: 'Main Sanctuary',
      description: 'Join us for engaging Bible study and fellowship as we grow together in God\'s Word.',
      image_url: 'https://images.unsplash.com/photo-1653133672754-82025e7e9074?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '2',
      title: 'Sunday Morning Service',
      date: 'Every Sunday',
      time: '11:00 AM CST',
      location: 'Main Sanctuary',
      description: 'Join us for powerful worship, anointed preaching, and genuine fellowship every Sunday morning.',
      image_url: 'https://images.unsplash.com/photo-1653133672754-82025e7e9074?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '3',
      title: 'Leadership Training',
      date: 'Every Monday',
      time: '6:00 PM CST',
      location: 'Fellowship Hall',
      description: 'Equipping and activating the next generation of leaders who will carry the fire of God\'s presence.',
      image_url: 'https://images.unsplash.com/photo-1768776184929-614344eecfad?crop=entropy&cs=srgb&fm=jpg&q=85',
      cancelled_notice: 'No Leadership Class this Monday — June 1, 2026'
    },
    {
      id: '4',
      title: 'Bible Study',
      date: 'Every Tuesday',
      time: '6:30 PM CST',
      location: 'Main Sanctuary',
      description: 'Come together to dive deep into God\'s Word and discover practical truths for daily living.',
      image_url: '/generated/bible-study.png',
      cancelled_notice: 'Bible Study is cancelled this week — Tuesday, June 2, 2026'
    },
    {
      id: '5',
      title: 'Prayer Line',
      date: 'Monday - Friday',
      time: '12:00 PM CST',
      location: 'Conference Call: (609) 663-1347',
      description: 'Join us daily in prayer as we seek God\'s face and intercede for our church, families, and community.',
      image_url: 'https://images.unsplash.com/photo-1609234656388-0ff363383899?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '6',
      title: 'Happy Father\'s Day',
      date: 'June 21st, 2026',
      time: 'Sunday School 10:00 AM | Morning Worship 11:00 AM',
      location: '5700 East 191st St., Belton, MO 64012',
      description: 'Honoring the Heart. Celebrating the Legacy. Thanking Our Fathers. "As for me and my house, we will serve the Lord." – Joshua 24:15. Behind every strong family is a Godly Father. We honor, we appreciate, we celebrate you!',
      image_url: '/flyers/fathers-day.png',
      featured: true
    },
    {
      id: '9',
      title: 'Syracuse, New York Ministry',
      date: 'July 30 – Aug 3, 2026',
      time: 'All Day',
      location: 'Syracuse, New York',
      description: 'Apostle Yolanda Browne will be ministering and preaching in Syracuse, New York. If you would like to attend, please contact the Church Liaison Min. Emarsis Williams for additional information.',
      image_url: 'https://images.unsplash.com/photo-1609234656388-0ff363383899?crop=entropy&cs=srgb&fm=jpg&q=85',
      requires_liaison: true
    },
    {
      id: '10',
      title: '100 Women in Red Conference',
      date: 'August 15 – 16, 2026',
      time: 'All Day',
      location: 'South Bend, Indiana',
      description: 'Apostle Yolanda Browne will be ministering at the 100 Women in Red Conference in South Bend, Indiana. If you would like to attend, please contact the Church Liaison Min. Emarsis Williams for additional information.',
      image_url: '/generated/100-women-in-red.png',
      requires_liaison: true
    }
  ];

  const displayEvents = events.length > 0 ? events : defaultEvents;
  
  // Separate featured event (Mother's Tea) from other events
  const featuredEvent = displayEvents.find(event => event.featured);
  const regularEvents = displayEvents.filter(event => !event.featured);

  return (
    <div data-testid="events-page" className="min-h-screen">
      <section 
        className="py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #DEB887 0%, #D2B48C 50%, #BC8F8F 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '500px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute top-20 left-20 text-[#8B4513]/5 transform rotate-45">
          <svg width="180" height="180" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-primary">What's Happening</span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-foreground mt-4 drop-shadow-sm">
              Upcoming Events
            </h1>
            <p className="text-xl text-muted-foreground mt-6 max-w-3xl mx-auto">
              Connect, grow, and serve with us through our various ministries and events.
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading events...</p>
            </div>
          ) : (
            <>
              {/* Featured Event - Father's Day Flyer */}
              {featuredEvent && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="mb-16"
                >
                  <div className="text-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                      Featured Event
                    </h2>
                    <div className="w-24 h-1 bg-primary mx-auto"></div>
                  </div>
                  <div 
                    data-testid="featured-event-fathers-day"
                    className="max-w-4xl mx-auto bg-white/95 backdrop-blur-sm rounded-3xl overflow-hidden shadow-2xl"
                  >
                    <img
                      src={featuredEvent.image_url}
                      alt={featuredEvent.title}
                      className="w-full h-auto"
                    />
                  </div>
                </motion.div>
              )}

              {/* Regular Events Grid */}
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-heading font-bold text-foreground">
                  Weekly Schedule
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`event-card-${index}`}
                  className="group bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-white/95 text-primary px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                      {event.date}
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <h3 className="text-xl font-heading font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                      {event.title}
                    </h3>

                    {event.cancelled_notice && (
                      <div
                        data-testid={`cancelled-banner-${index}`}
                        className="flex items-center gap-2 bg-red-50 border-l-4 border-red-500 text-red-700 px-3 py-2 rounded-r-md text-xs font-semibold"
                      >
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        {event.cancelled_notice}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4 text-primary" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                    
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {event.description}
                    </p>
                    
                    <button
                      data-testid={`register-event-${index}`}
                      className="w-full bg-gradient-to-r from-primary to-amber-600 text-white py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 mt-4"
                    >
                      Learn More
                    </button>

                    {event.requires_liaison && (
                      <a
                        href="tel:8168760470"
                        data-testid={`contact-liaison-${index}`}
                        className="w-full flex items-center justify-center gap-2 bg-white border-2 border-primary text-primary py-2 rounded-lg font-semibold hover:bg-primary hover:text-white transition-all duration-300 text-sm"
                      >
                        <Mail className="w-4 h-4" />
                        Contact Church Liaison
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
            </>
          )}
        </div>
      </section>

      <section 
        data-testid="calendar-cta-section" 
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B7355 0%, #A0826D 50%, #8B7355 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '700px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        <div className="absolute bottom-10 right-10 text-white/5 transform rotate-12">
          <svg width="150" height="150" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="10" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-2xl"
          >
            <Calendar className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Want to Stay Updated?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Connect with us to receive notifications about upcoming events, special services, and community gatherings.
            </p>
            <a
              href="tel:8168760470"
              data-testid="contact-for-updates-button"
              className="inline-block bg-gradient-to-r from-primary to-amber-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-2xl transition-all duration-300"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
